import {
    Aborter,
    StorageURL,
    ContainerURL,
    BlockBlobURL,
    AnonymousCredential
} from "@azure/storage-blob";
import NetworkManager from "./NetworkManager";

const networkManager = NetworkManager.instance;

/**
 * Wrapper function for stream reading.
 * Reads data from a stream until "end" is emitted.
 * Returns a buffer when done.
 * @param {NodeJS.ReadableStream} stream The stream to read from
 * @returns {Promise<Buffer>} The buffer filled with the stream data
 */
function streamToBuffer(stream) {
    return new Promise((resolve, reject) => {
        const chunks = [];
        stream
            .on("data", chunk => chunks.push(chunk))
            .on("end", () => resolve(Buffer.concat(chunks)))
            .on("error", reject);
    });
}

/**
 * A blob storage always has a container in its top most filesystem.
 * In a container there are an arbitrary amount of blobs.
 * A blob must have a string name and may contain "/" in it, to simulate a folder.
 * 
 * These virtual folders can be listed with this.listFolders(), the blobs inside a folder with this.listBlobs().
 * If you want to ignore the virutal folder structure, use this.listBlobsRecursive() which lists simply all blobs.
 */
export default class BlobStorageManager {

    /**
     * @returns {BlobStorageManager}
     */
    static get instance() {
        if (!BlobStorageManager._instance) BlobStorageManager._instance = new BlobStorageManager();
        return BlobStorageManager._instance;
    }

    /**
     * Converts an amount of bytes to a readable amount string
     * @param {number} bytes The amount of bytes
     */
    static humanFileSize( bytes ) {
        const i = Math.floor( Math.log(bytes) / Math.log(1024) );
        return ( bytes / Math.pow(1024, i) ).toFixed(1) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    /**
     * Creates a blob
     * @param {BlockBlobURL} blobUrl The full name of the blob (including virtual folders)
     * @param {string | ArrayBuffer | Blob | Buffer} content The content to store in the blob
     * @param {"application/zip" | "application/json" | "text/plain" | "application/octet-stream"} blobContentType The outer most MIME-Type of the blob content. If the content is a Blob Object itself, its filetype will be used and this parameter is ignored
     * @param {import("@azure/storage-blob/src/models").IMetadata} metadata Arbitrary key-value string pairs stored in an object eg. { "key": "value" }
     * @param {string} blobContentDisposition Helpful for friendly download names. Example: "attachment; filename=blob123.zip" will create the file blob123.zip when downloading
     */
    async createBlob( blobUrl, content, blobContentType = "application/octet-stream", metadata = {}, blobContentDisposition = undefined ) {
        
        // convert Buffer to ArrayBuffer
        if (content instanceof Buffer) content = content.buffer;

        let contentLength = 0;

        // determine the content length
        if (typeof content === "string") {
            contentLength = content.length;
        } else if (content instanceof ArrayBuffer) {
            contentLength = content.byteLength;
        } else if (content instanceof Blob) {
            contentLength = content.size;
            if (content.type) blobContentType = content.type;
        } else {
            throw new TypeError("Unsupported object type of the content! Check the definition of this function.");
        }

        // upload
        const res = await blobUrl
            .upload(
                Aborter.none,
                content,
                contentLength,
                {
                    metadata,
                    blobHTTPHeaders: {
                        blobContentType,
                        blobContentDisposition
                    }
                }
            );

        // check error
        if (res.errorCode) throw new Error(`Blob creation failed with error code "${res.errorCode}"`);
        
    }

    /**
     * Downloads a single blob
     * @param {BlockBlobURL} blobUrl The full name of the blob (including virtual folders)
     */
    async downloadBlob( blobUrl ) {
        const res = await blobUrl.download(Aborter.none, 0);

        if (res.errorCode) throw new Error(`Blod download failed with error code "${res.errorCode}"`);
        return await streamToBuffer(res.readableStreamBody);
    }

    /**
     * Fetches the properties of a blob. 
     * Since the azure framework just throws an empty error when the blob does not exist, I implemented this function myself.
     * If however this function works properly with a new version of @azure/storage-blob, feel free to use it here.
     * @param {BlockBlobURL | string} blobUrl The full name of the blob (including virtual folders)
     */
    async getBlobProperties( blobUrl ) {
        if (typeof blobUrl !== "string") blobUrl = blobUrl.url;

        const res = await fetch(blobUrl, { method: "HEAD" });

        return {
            exists: res.status !== 404,
            filename: this.getContentFileNameOfBlob(res.headers.get("Content-Disposition")) || "Datei",
            url: blobUrl,
            contentType: res.headers.get("Content-Type"),
            contentLength: res.headers.get("Content-Length"),
            date: new Date(res.headers.get("Date"))
        };
        
    }

    /**
     * Deletes a blob
     * @param {BlockBlobURL} blobUrl The full name of the blob (including virtual folders)
     */
    async deleteBlob( blobUrl ) {
        const res = await blobUrl.delete(Aborter.none);
        
        if (res.errorCode) throw new Error(`Blob deletion failed with error code "${res.errorCode}"`);
    }

    /**
     * Extracts the filename property from the contentDisposition of a blob.
     * @param {string} contentDisposition The contentDisposition property of the blob
     */
    getContentFileNameOfBlob( contentDisposition ) {
        const res = /filename="(.*)"/g.exec(contentDisposition);
        return res ? res[1] : null;
    }

    /**
     * Creates a ContainerURL instance from an URL string with the anonymous pipeline.
     * Use this when you received an URL string created by generateSasUrl().
     * Since the return type of generateSasUrl() is the URL instance itself this is only necessary if it is sent over the web or stored as a string somewhere.
     * @param {string} url The full url string to the container with its SAS params appended
     */
    getAnonymousContainerUrl( url ) {
        return new ContainerURL(url, StorageURL.newPipeline( new AnonymousCredential() ));
    }

    /**
     * Creates a BlockBlobURL instance from an URL string with the anonymous pipeline.
     * Use this when you received an URL string created by generateSasUrl().
     * Since the return type of generateSasUrl() is the URL instance itself this is only necessary if it is sent over the web or stored as a string somewhere.
     * @param {string} url The full url string to the blob with its SAS params appended
     */
    getAnonymousBlobUrl( url ) {
        return new BlockBlobURL(url, StorageURL.newPipeline( new AnonymousCredential() ));
    }

    /**
     * Requests blob upload URLs from the server
     * @param {number} amount The amount of URLs
     * @returns {Promise<string[]>} The urls to upload the files with
     */
    async getUploadUrls( amount ) {
        return await networkManager.fetch("/fileUploadUrls", "GET", { amount });
    }

}